import type { FormSchema, GeneratorOptions } from '@cli-types/core-engine/schema.js'
import type { StepResult } from '@cli-types/next-adapter/forms.js'
import { createGeneratedFile } from '@cli-utils/files.js'
import {
  humanizeName,
  quoteLiteral,
  toCamelCase,
  toKebabCase,
  toPascalCase
} from '@cli-utils/text.js'
import { getAllFormSchemaFields } from '@core-engine/schema/field-helpers/form-fields/get-all-form-schema-fields.js'
import { resolveSchemaActionDir } from '@next-adapter/config/paths/resolve-schema-action-dir.js'
import { afterHook } from '@next-adapter/generators/actions/lifecycle-hook-emissions/after-hook.js'
import { beforeHook } from '@next-adapter/generators/actions/lifecycle-hook-emissions/before-hook.js'
import { lifecycleContext } from '@next-adapter/generators/actions/lifecycle-hook-emissions/lifecycle-context.js'
import { lifecycleHookImport } from '@next-adapter/generators/actions/lifecycle-hook-emissions/lifecycle-hook-import.js'
import { renderExclusiveDateUpperBoundHelper } from '@next-adapter/templates/renderers/generators/actions/date-range/exclusive-date-upper-bound-helper.js'
import { renderFormActionFiles } from '@next-adapter/templates/renderers/generators/form-pipeline/form-actions/generate-form-actions.js'
import { generateMailchimpCode } from './generate-mailchimp-code.js'
import { getSearchableSubmissionFields } from './get-searchable-submission-fields.js'
import { getSortableSubmissionFields } from './get-sortable-submission-fields.js'

export function generateFormActions(
  schema: FormSchema,
  actionsDir: string,
  options: GeneratorOptions
): StepResult {
  const schemaActionDir = resolveSchemaActionDir(actionsDir, schema.name, options.outputNamespace)
  const formName = schema.name
  const tableName = `${toCamelCase(formName)}Submissions`
  const pascal = toPascalCase(formName)
  const kebab = toKebabCase(formName)
  const formSettingsKey = kebab
  const fields = getAllFormSchemaFields(schema)
  const searchFields = getSearchableSubmissionFields(fields)
  const sortableFields = getSortableSubmissionFields(schema, fields)
  const nextMajor = options.nextMajorVersion ?? 16
  const cacheInvalidationFn = nextMajor >= 16 ? 'updateTag' : 'revalidateTag'
  const cacheReadImport =
    nextMajor >= 16
      ? `import { cacheLife, cacheTag } from 'next/cache'`
      : `import { unstable_cacheTag as cacheTag } from 'next/cache'`
  const cacheLifeLine = nextMajor >= 16 ? `\n  cacheLife('max')` : ''
  const idPlaceholder = '$' + '{id}'

  const dataFields = fields
    .map((f) => {
      let tsType = 'string'
      if (f.type === 'number') tsType = 'number'
      else if (f.type === 'checkbox') tsType = f.options?.length ? 'string[]' : 'boolean'
      else if (f.type === 'multiselect') tsType = 'string[]'
      else if (f.type === 'list') {
        tsType = f.fields && f.fields.length > 0 ? 'Record<string, unknown>[]' : 'string[]'
      }
      const isReq = f.required && !f.condition
      const opt = isReq ? '' : '?'
      const nullable = isReq ? '' : ' | null'
      return `  ${f.name}${opt}: ${tsType}${nullable}`
    })
    .join('\n')

  const validation = fields
    .filter((f) => f.required && !f.condition)
    .map((f) => {
      const error = `return { success: false, error: ${quoteLiteral(`${f.label || humanizeName(f.name ?? '')} is required`)} }`
      let check: string
      if (f.type === 'checkbox' && f.options?.length) {
        check = `!Array.isArray(data.${f.name}) || data.${f.name}.length === 0`
      } else if (f.type === 'checkbox') {
        check = `data.${f.name} !== true`
      } else if (f.type === 'number') {
        check = `data.${f.name} === undefined || data.${f.name} === null`
      } else if (f.type === 'multiselect' || f.type === 'list') {
        check = `!Array.isArray(data.${f.name}) || data.${f.name}.length === 0`
      } else {
        check = `data.${f.name} === undefined || data.${f.name} === null || data.${f.name} === ''`
      }
      return `if (${check}) {\n      ${error}\n    }`
    })
    .join('\n    ')

  const mailchimpImport = schema.mailchimp
    ? `\nimport { addToMailchimpAudience } from '@admin/actions/mailchimp/add-to-mailchimp-audience'`
    : ''
  const mailchimpCall = generateMailchimpCode(schema, fields)

  const emailField = fields.find((f) => f.type === 'email')
  const submitterEmailField = emailField?.name ?? null
  const schemaNotificationFallback = schema.notificationEmail
    ? `[${JSON.stringify(schema.notificationEmail)}]`
    : 'process.env.NOTIFICATION_EMAIL\n        ? [process.env.NOTIFICATION_EMAIL]\n        : []'

  const sortableColumnsMap = sortableFields
    .map((field) => `  '${field}': ${tableName}.${field}`)
    .join(',\n')

  const searchMap = searchFields
    .map((field) => `          ilike(${tableName}.${field}, searchTerm)`)
    .join(',\n')

  const getSubmissionsDrizzleImports = ['and', 'asc', 'desc', 'gte', 'lt', 'sql']
  if (searchFields.length > 0) {
    getSubmissionsDrizzleImports.push('ilike', 'or')
  }

  const createLifecycleContext = lifecycleContext(
    formName,
    `,\n      input: data as unknown as Record<string, unknown>,\n      changedFields`,
    {
      schemaType: 'form',
      sourceType: 'server_action',
      userExpression: 'session?.user ?? null'
    }
  )
  const createResultLifecycleContext = lifecycleContext(
    formName,
    `,\n      entityId: created${pascal}Submission.id,\n      input: data as unknown as Record<string, unknown>,\n      data: created${pascal}Submission as unknown as Record<string, unknown>,\n      changedFields`,
    {
      schemaType: 'form',
      sourceType: 'server_action',
      userExpression: 'session?.user ?? null'
    }
  )
  const deleteLifecycleContext = lifecycleContext(
    formName,
    `,\n      entityId: id,\n      changedFields: []`,
    {
      schemaType: 'form',
      sourceType: 'admin_ui',
      userExpression: 'session?.user ?? null'
    }
  )

  const hasExport = schema.actions?.export === true

  const exportBarrelLine = hasExport
    ? `\nexport { export${pascal}SubmissionsCSV } from './export-${kebab}-submissions-csv'`
    : ''

  const rendered = renderFormActionFiles({
    searchMap,
    pascal,
    dataFields,
    formName,
    idPlaceholder,
    sort: getSubmissionsDrizzleImports.sort().join(', '),
    tableName,
    cacheReadImport,
    exclusiveDateUpperBoundSource: renderExclusiveDateUpperBoundHelper(),
    sortableColumnsMap,
    cacheLifeLine,
    cacheInvalidationFn,
    mailchimpImport,
    lifecycleHookImport: lifecycleHookImport(),
    createLifecycleContext,
    beforeCreateHook: beforeHook('beforeCreate', 'lifecycleContext'),
    validation,
    formSettingsKey,
    schemaNotificationFallback,
    schemaLabel: schema.label,
    submitterEmailFieldNull: submitterEmailField ? `data.${submitterEmailField}` : 'null',
    mailchimpCall,
    createResultLifecycleContext,
    afterCreateHook: afterHook('afterCreate', `created${pascal}SubmissionContext`),
    deleteLifecycleContext,
    beforeDeleteHook: beforeHook('beforeDelete', 'lifecycleContext'),
    afterDeleteHook: afterHook('afterDelete', 'lifecycleContext'),
    kebab,
    exportBarrelLine
  })

  const files: { name: string; content: string }[] = [
    { name: 'types.ts', content: rendered.typesContent },
    { name: `get-${kebab}-submissions.ts`, content: rendered.getSubmissionsContent },
    { name: `get-${kebab}-submission.ts`, content: rendered.getSubmissionContent },
    { name: `create-${kebab}-submission.ts`, content: rendered.createContent },
    { name: `delete-${kebab}-submission.ts`, content: rendered.deleteContent },
    { name: `delete-bulk-${kebab}-submissions.ts`, content: rendered.deleteBulkContent }
  ]
  if (hasExport) {
    files.push({ name: `export-${kebab}-submissions-csv.ts`, content: rendered.exportCsvContent })
  }
  files.push({ name: 'index.ts', content: rendered.barrelContent })

  return {
    files: files.map((file) => createGeneratedFile(`${schemaActionDir}/${file.name}`, file.content))
  }
}
